import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Upload, FileText, Wallet, Users, Plus, Check } from 'lucide-react';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import ExportMonthDialog from '../components/ExportMonthDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminInvoices = () => {
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedClientFilter, setSelectedClientFilter] = useState('all');
  const [formData, setFormData] = useState({ user_id: '', order_id: '', amount: '', due_date: '' });
  const [file, setFile] = useState(null);
  const [paymentInputs, setPaymentInputs] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [c, i, o] = await Promise.all([
        axios.get(`${API}/admin/clients`, { withCredentials: true }),
        axios.get(`${API}/invoices`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true })
      ]);
      setClients(c.data);
      setInvoices(i.data);
      setOrders(o.data);
    } catch (e) {
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Selectează un fișier'); return; }
    try {
      const fd = new FormData();
      fd.append('user_id', formData.user_id);
      fd.append('amount', formData.amount);
      fd.append('due_date', formData.due_date);
      if (formData.order_id) fd.append('order_id', formData.order_id);
      fd.append('file', file);
      await axios.post(`${API}/admin/invoices`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Factură încărcată');
      setOpen(false);
      setFormData({ user_id: '', order_id: '', amount: '', due_date: '' });
      setFile(null);
      fetchData();
    } catch {
      toast.error('Eroare la încărcarea facturii');
    }
  };

  const addPayment = async (invoiceId) => {
    const raw = paymentInputs[invoiceId];
    const amount = parseFloat(raw?.amount);
    if (!amount || amount <= 0) { toast.error('Introdu o sumă validă'); return; }
    try {
      await axios.post(
        `${API}/admin/invoices/${invoiceId}/payments`,
        { amount, note: raw?.note || '' },
        { withCredentials: true }
      );
      toast.success('Plată înregistrată. Client anunțat prin email.');
      setPaymentInputs({ ...paymentInputs, [invoiceId]: { amount: '', note: '' } });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la înregistrarea plății');
    }
  };

  const markFullyPaid = async (invoiceId) => {
    try {
      await axios.put(`${API}/admin/invoices/${invoiceId}/paid`, {}, { withCredentials: true });
      toast.success('Marcată ca plătită integral');
      fetchData();
    } catch { toast.error('Eroare'); }
  };

  const filteredInvoices = useMemo(() => {
    if (selectedClientFilter === 'all') return invoices;
    return invoices.filter((inv) => inv.user_id === selectedClientFilter);
  }, [invoices, selectedClientFilter]);

  const selectedClient = clients.find((c) => c.id === selectedClientFilter);
  const clientStats = useMemo(() => {
    if (selectedClientFilter === 'all') return null;
    const clientInvoices = invoices.filter((inv) => inv.user_id === selectedClientFilter);
    const total = clientInvoices.reduce((s, i) => s + i.amount, 0);
    const paid = clientInvoices.reduce((s, i) => s + (i.paid_amount || 0), 0);
    return {
      total,
      paid,
      remaining: total - paid,
      count: clientInvoices.length,
      unpaidCount: clientInvoices.filter((i) => !i.is_paid).length
    };
  }, [invoices, selectedClientFilter]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p style={{ fontFamily: 'IBM Plex Sans' }}>Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Gestionare Facturi
          </h1>

          <div className="flex flex-wrap gap-2">
            <ExportMonthDialog
              endpoint="/admin/export/invoices"
              filenamePrefix="facturi"
              triggerLabel="Export lunar Excel"
              testId="export-invoices-button"
            />
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="upload-invoice-button">
                <Upload className="w-5 h-5 mr-2" /> Încarcă Factură
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Outfit' }}>Încarcă Factură Nouă</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUploadSubmit} className="space-y-4 mt-4">
                <div>
                  <Label>Selectează Client</Label>
                  <Select value={formData.user_id} onValueChange={(v) => setFormData({ ...formData, user_id: v, order_id: '' })} required>
                    <SelectTrigger data-testid="client-select"><SelectValue placeholder="Alege client..." /></SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.user_id && (
                  <div>
                    <Label>Leagă de comandă (opțional - număr identic)</Label>
                    <Select value={formData.order_id} onValueChange={(v) => setFormData({ ...formData, order_id: v })}>
                      <SelectTrigger data-testid="order-select"><SelectValue placeholder="Fără legătură..." /></SelectTrigger>
                      <SelectContent>
                        {orders.filter((o) => o.user_id === formData.user_id).map((o) => (
                          <SelectItem key={o.id} value={o.id}>#{o.id.substring(0, 8).toUpperCase()} - {o.total} RON ({o.status})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Sumă (RON)</Label>
                  <Input type="number" step="0.01" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} required data-testid="invoice-amount-input" />
                </div>
                <div>
                  <Label>Data Scadentă</Label>
                  <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} required data-testid="invoice-due-date-input" />
                </div>
                <div>
                  <Label>Fișier Factură (PDF/Imagine)</Label>
                  <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} required data-testid="invoice-file-input" />
                </div>
                <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-800 text-white" data-testid="submit-invoice-button">Încarcă</Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Client Filter */}
        <Card className="mb-6 border border-slate-200 shadow-sm">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <Users className="w-5 h-5 text-blue-900 shrink-0" />
              <Label className="whitespace-nowrap" style={{ fontFamily: 'IBM Plex Sans' }}>Filtrează după client:</Label>
              <Select value={selectedClientFilter} onValueChange={setSelectedClientFilter}>
                <SelectTrigger className="w-full sm:w-96" data-testid="filter-client-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toți clienții</SelectItem>
                  {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} ({c.email})</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Client Stats Panel */}
        {selectedClient && clientStats && (
          <Card className="mb-6 border border-blue-200 shadow-md bg-gradient-to-br from-blue-50 to-white" data-testid="client-stats-panel">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2" style={{ fontFamily: 'Outfit' }}>
                <Wallet className="w-6 h-6 text-blue-900" />
                <span>{selectedClient.name}</span>
                <span className="text-sm font-normal text-slate-600">({selectedClient.email})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>Total facturi</p>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }} data-testid="stat-total">{clientStats.total.toFixed(2)} RON</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>Achitat</p>
                  <p className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Outfit' }} data-testid="stat-paid">{clientStats.paid.toFixed(2)} RON</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>Sold neachitat</p>
                  <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Outfit' }} data-testid="stat-remaining">{clientStats.remaining.toFixed(2)} RON</p>
                </div>
                <div>
                  <p className="text-xs text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>Facturi</p>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>{clientStats.count} <span className="text-sm text-red-600 font-normal">({clientStats.unpaidCount} neachit.)</span></p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {filteredInvoices.length === 0 ? (
          <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
            {selectedClientFilter === 'all' ? 'Nu există facturi.' : 'Acest client nu are facturi.'}
          </p>
        ) : (
          <div className="space-y-4">
            {filteredInvoices.map((invoice) => {
              const client = clients.find((c) => c.id === invoice.user_id);
              const paidAmount = invoice.paid_amount || 0;
              const remaining = invoice.amount - paidAmount;
              const displayNum = (invoice.order_id || invoice.id).substring(0, 8).toUpperCase();
              const paymentInput = paymentInputs[invoice.id] || { amount: '', note: '' };

              return (
                <Card key={invoice.id} className="border border-slate-200 shadow-sm" data-testid={`admin-invoice-${invoice.id}`}>
                  <CardHeader className="bg-slate-50 border-b border-slate-200">
                    <div className="flex flex-col sm:flex-row justify-between items-start gap-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <FileText className="w-6 h-6 text-blue-900" />
                        </div>
                        <div>
                          <CardTitle style={{ fontFamily: 'Outfit' }}>Factură #{displayNum}</CardTitle>
                          <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans' }}>
                            {client?.name || 'N/A'} ({client?.email || 'N/A'})
                          </p>
                        </div>
                      </div>
                      {invoice.is_paid ? (
                        <Badge className="bg-green-100 text-green-800 border border-green-200">
                          <Check className="w-3 h-3 mr-1" /> Achitată
                        </Badge>
                      ) : paidAmount > 0 ? (
                        <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-200">
                          Parțial: {paidAmount.toFixed(2)} / {invoice.amount.toFixed(2)} RON
                        </Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-800 border border-red-200">Neachitată</Badge>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="p-6 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" style={{ fontFamily: 'IBM Plex Sans' }}>
                      <div>
                        <p className="text-slate-600 text-xs">Total</p>
                        <p className="font-bold text-blue-900">{invoice.amount.toFixed(2)} RON</p>
                      </div>
                      <div>
                        <p className="text-slate-600 text-xs">Achitat</p>
                        <p className="font-bold text-green-600">{paidAmount.toFixed(2)} RON</p>
                      </div>
                      <div>
                        <p className="text-slate-600 text-xs">Rest de plată</p>
                        <p className="font-bold text-red-600" data-testid={`remaining-${invoice.id}`}>{remaining.toFixed(2)} RON</p>
                      </div>
                      <div>
                        <p className="text-slate-600 text-xs">Scadență</p>
                        <p className="font-semibold">{new Date(invoice.due_date).toLocaleDateString('ro-RO')}</p>
                      </div>
                    </div>

                    {invoice.payments?.length > 0 && (
                      <div className="border-t border-slate-200 pt-3">
                        <p className="text-xs font-semibold text-slate-700 mb-2" style={{ fontFamily: 'IBM Plex Sans' }}>Istoric plăți:</p>
                        <div className="space-y-1">
                          {invoice.payments.map((p, idx) => (
                            <div key={idx} className="flex justify-between text-xs bg-green-50 border border-green-100 rounded px-2 py-1" style={{ fontFamily: 'IBM Plex Sans' }}>
                              <span className="text-slate-600">{format(new Date(p.date), 'dd MMM yyyy, HH:mm', { locale: ro })}{p.note ? ` - ${p.note}` : ''}</span>
                              <span className="font-semibold text-green-700">+{p.amount.toFixed(2)} RON</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!invoice.is_paid && (
                      <div className="border-t border-slate-200 pt-3 space-y-2">
                        <p className="text-sm font-semibold text-slate-800" style={{ fontFamily: 'IBM Plex Sans' }}>Înregistrează plată:</p>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <Input
                            type="number"
                            step="0.01"
                            placeholder={`Max ${remaining.toFixed(2)}`}
                            value={paymentInput.amount}
                            onChange={(e) => setPaymentInputs({ ...paymentInputs, [invoice.id]: { ...paymentInput, amount: e.target.value } })}
                            className="sm:w-32"
                            data-testid={`payment-amount-${invoice.id}`}
                          />
                          <Input
                            type="text"
                            placeholder="Notă (opțional): cash, transfer..."
                            value={paymentInput.note}
                            onChange={(e) => setPaymentInputs({ ...paymentInputs, [invoice.id]: { ...paymentInput, note: e.target.value } })}
                            className="flex-1"
                            data-testid={`payment-note-${invoice.id}`}
                          />
                          <Button
                            onClick={() => addPayment(invoice.id)}
                            className="bg-blue-900 hover:bg-blue-800 text-white"
                            data-testid={`add-payment-${invoice.id}`}
                          >
                            <Plus className="w-4 h-4 mr-1" /> Încasează
                          </Button>
                          <Button
                            onClick={() => markFullyPaid(invoice.id)}
                            variant="outline"
                            className="border-green-600 text-green-700 hover:bg-green-50"
                            data-testid={`mark-fully-paid-${invoice.id}`}
                          >
                            <Check className="w-4 h-4 mr-1" /> Achitată total
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInvoices;
